/**
 * LoginScreen — email + password → JWT.
 *
 * Visual: centred card on a flag-coloured backdrop. Form validation via
 * react-hook-form + zod (overkill for two fields, but the rest of the
 * admin will use the same pattern — keep it consistent from day one).
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation } from "react-router-dom";
import { z } from "zod";

import { useAuth } from "../auth/AuthProvider";
import { cn } from "../lib/cn";

const LoginSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Enter your password"),
});
type LoginInput = z.infer<typeof LoginSchema>;

export function LoginScreen() {
  const { isAuthenticated, login } = useAuth();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: "", password: "" },
  });

  if (isAuthenticated) {
    const from = (location.state as { from?: string } | null)?.from ?? "/dashboard";
    return <Navigate to={from} replace />;
  }

  async function onSubmit(values: LoginInput) {
    setServerError(null);
    try {
      await login(values.email, values.password);
      // RequireAuth-driven Navigate kicks in on next render.
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "Login failed");
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-stone-100 p-4">
      <div className="w-full max-w-sm rounded-lg border border-stone-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-3">
          <div
            aria-hidden
            className="size-10 rounded-md"
            style={{
              background:
                "linear-gradient(135deg, var(--color-flag-red) 0%, var(--color-flag-red) 33%, var(--color-flag-white) 33%, var(--color-flag-white) 66%, var(--color-flag-green) 66%, var(--color-flag-green) 100%)",
            }}
          />
          <div>
            <h1 className="text-base font-semibold leading-tight">KRCL Admin</h1>
            <p className="text-xs text-stone-500">Committee sign-in</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <Field label="Email" error={errors.email?.message}>
            <input
              type="email"
              autoComplete="username"
              {...register("email")}
              className={cn(
                "w-full rounded-md border bg-white px-3 py-2 text-sm",
                "border-stone-300 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500",
              )}
            />
          </Field>

          <Field label="Password" error={errors.password?.message}>
            <input
              type="password"
              autoComplete="current-password"
              {...register("password")}
              className={cn(
                "w-full rounded-md border bg-white px-3 py-2 text-sm",
                "border-stone-300 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500",
              )}
            />
          </Field>

          {serverError && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{serverError}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={cn(
              "inline-flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium",
              "bg-red-600 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60",
            )}
          >
            {isSubmitting && <Loader2 className="size-4 animate-spin" />}
            Sign in
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-stone-400">
          Per KRCL constitution §4 — committee only
        </p>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-stone-700">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}
